import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Quill } from "react-quill";
import ImageResize from "quill-image-resize-module-react";
import { ImageDrop } from "quill-image-drop-module";
import axios from "axios";
import heic2any from "heic2any";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Typography,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import API_BASE_URL from "../../utils/api";
import QuillEditor from "../../components/QuillEditor/QuillEditor";
import "./BlogForm.css";

Quill.register("modules/imageResize", ImageResize);
Quill.register("modules/imageDrop", ImageDrop);
const Size = Quill.import("attributors/style/size");
Size.whitelist = [
  "12px",
  "14px",
  "16px",
  "17px",
  "18px",
  "24px",
  "32px",
  "48px",
];
Quill.register(Size, true);

const formats = [
  "header",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "align",
  "link",
  "image",
  "code-block",
];
const extractAllImages = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  return Array.from(doc.querySelectorAll("img")).map((img) => img.src);
};

function extractImagesWithCaptions(html = "") {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const out = [];
  doc.querySelectorAll("img").forEach((img) => {
    const container = img.closest("figure, p, div") || img.parentElement;
    let legacy = "";
    const next = container?.nextElementSibling;
    if (
      next &&
      next.tagName === "P" &&
      next.classList.contains("img-caption")
    ) {
      legacy = next.textContent || "";
    }
    const caption =
      img.getAttribute("data-caption") ||
      img.getAttribute("alt") ||
      legacy ||
      "";
    out.push({ src: img.src, caption });
  });
  return out;
}

function applyCaptionsToHTML(html = "", images = []) {
  const bySrc = Object.fromEntries(images.map((i) => [i.src, i.caption || ""]));

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  doc.querySelectorAll("img").forEach((img) => {
    const cap = bySrc[img.src];
    if (cap !== undefined) {
      img.setAttribute("data-caption", cap);
      img.setAttribute("alt", cap);
    }
    const container = img.closest("figure, p, div") || img.parentElement;
    const n = container?.nextElementSibling;
    if (n && n.tagName === "P" && n.classList.contains("img-caption")) {
      n.remove(); // strip legacy blocks from editor content
    }
  });

  return doc.body.innerHTML;
}

export const BlogForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [type, setType] = useState("blog");
  const [title, setTitle] = useState("");
  const [externalUrl, setExternalUrl] = useState("");
  const [externalDate, setExternalDate] = useState("");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(!!id);
  const [images, setImages] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const quillRef = useRef(null);

  useEffect(() => {
    if (id) {
      axios
        .get(`${API_BASE_URL}/blogs/${id}`)
        .then((res) => {
          setTitle(res.data.title);
          setContent(res.data.content);
          setType(res.data.type);
          setImages(extractImagesWithCaptions(res.data.content || ""));
          if (res.data.type === "news" && res.data.isExternal) {
            setExternalUrl(res.data.externalUrl || "");
            if (res.data.externalPublishedAt) {
              setExternalDate(res.data.externalPublishedAt.slice(0, 10));
            }
          }
          setLoading(false);
        })
        .catch((err) => {
          console.error("Failed to load blog", err);
          alert("Failed to load blog");
          navigate("/blogs");
        });
    }
  }, [id, navigate]);
  // Remove a specific image from content
  const handleRemoveImage = (imgSrc) => {
    setImages((prev) => prev.filter((im) => im.src !== imgSrc));

    const editor = quillRef.current?.getEditor();
    if (editor) {
      const imgDom = Array.from(editor.root.querySelectorAll("img")).find(
        (i) => i.src === imgSrc
      );
      if (imgDom) {
        const blot = Quill.find(imgDom);
        const idx = editor.getIndex(blot);
        editor.deleteText(idx, 1, "user");
      }
    }
  };
  /**
   * Custom Image Upload Handler
   */
  const imageHandler = useCallback(() => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.setAttribute("multiple", "true");
    input.click();

    input.onchange = async () => {
      const files = Array.from(input.files);
      if (!files.length) return;

      for (const file of files) {
        let convertedFile = file;

        // Check size (limit 5MB)
        if (file.size > 6 * 1024 * 1024) {
          alert(`${file.name} exceeds 5MB and will be skipped.`);
          continue;
        }

        // HEIC to JPEG conversion
        const isHeic =
          file.type === "image/heic" ||
          file.type === "image/heif" ||
          file.name.toLowerCase().endsWith(".heic") ||
          file.name.toLowerCase().endsWith(".heif");

        if (isHeic) {
          try {
            const blob = await heic2any({ blob: file, toType: "image/jpeg" });
            convertedFile = new File(
              [blob],
              file.name.replace(/\.[^/.]+$/, ".jpg"),
              { type: "image/jpeg" }
            );
          } catch (error) {
            console.error("HEIC conversion failed:", error);
            alert(`Failed to convert ${file.name}.`);
            continue;
          }
        }

        // Upload to S3
        const formData = new FormData();
        formData.append("image", convertedFile);
        formData.append("blogId", id || "new");

        try {
          const res = await axios.post(
            `${API_BASE_URL}/blogs/upload`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );

          const imageUrl = res.data.url;
          setImages((prev) => [...prev, { src: imageUrl, caption: "" }]);
          const editor = quillRef.current?.getEditor();
          if (!editor) continue;

          const range = editor.getSelection(true) || {
            index: editor.getLength(),
          };
          editor.insertEmbed(range.index, "image", imageUrl);
          editor.setSelection(range.index + 1);
        } catch (err) {
          console.error("Image upload failed", err);
          alert(`Failed to upload ${file.name}`);
        }
      }
    };
  }, [quillRef, id]);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          [{ size: ["16px", "17px", "18px"] }],
          ["bold", "italic", "underline", "strike"],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ align: [] }],
          ["link", "image", "code-block"],
          ["clean"],
        ],
        handlers: { image: imageHandler },
      },
      imageResize: { parchment: Quill.import("parchment") },
      imageDrop: true,
    }),
    [imageHandler]
  );

  /**
   * Handle Blog Submit
   */
  const handleSubmit = async () => {
    const htmlWithAttrs = applyCaptionsToHTML(content, images);

    if (!title.trim() && !externalUrl.trim()) {
      alert("Please provide a title or an article URL.");
      return;
    }

    const payload = {
      title: title.trim(),
      content: htmlWithAttrs,
      type,
      externalUrl: type === "news" ? externalUrl.trim() : "",
      externalPublishedAt: type === "news" ? externalDate : "",
    };

    if (images.length > 0) {
      payload.imageUrl = images[0].src;
    }

    try {
      if (id) {
        await axios.put(`${API_BASE_URL}/blogs/${id}`, payload, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        setSnackbarMessage("Post updated successfully");
      } else {
        if (!("imageUrl" in payload)) {
          payload.imageUrl = null;
        }
        await axios.post(`${API_BASE_URL}/blogs/create`, payload, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        setSnackbarMessage("Post created successfully");
      }

      setSnackbarOpen(true);
      setTimeout(() => navigate("/blogs"), 1500);
    } catch (err) {
      console.error("Post failed", err);
      alert("Post failed");
    }
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "auto",
        padding: { xs: "20px", sm: "30px", md: "1px 80px" },
        margin: "0px auto",
        border: "none",
        backgroundColor: "#ffffff",
        borderRadius: "8px",
      }}
    >
      <Typography variant="h5" gutterBottom>
        {id ? "Update Blog / News" : "Create Blog / News"}
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Chip
          label="Blog"
          clickable
          onClick={() => setType("blog")}
          color={type === "blog" ? "primary" : "default"}
        />
        <Chip
          label="News"
          clickable
          onClick={() => setType("news")}
          sx={{ ml: 1 }}
          color={type === "news" ? "primary" : "default"}
        />
      </Box>

      <Box
        component="input"
        type="text"
        placeholder="Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        sx={{
          width: "100%",
          p: 1.5,
          mb: 3,
          borderRadius: 1,
          border: "1px solid #ccc",
          fontSize: "16px",
        }}
      />
      {type === "news" && (
        <Box sx={{ mb: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="caption">
              External article URL (optional)
            </Typography>
            <input
              type="url"
              placeholder="https://example.com/article-about-us"
              value={externalUrl}
              onChange={(e) => setExternalUrl(e.target.value)}
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: 6,
                border: "1px solid #ccc",
                fontSize: "14px",
              }}
            />
            <Typography variant="caption" color="text.secondary">
              If you provide a URL, we’ll try to pull the title, description
              (2–3 lines), and image from the article and store it.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              maxWidth: 220,
            }}
          >
            <Typography variant="caption">
              Date the article was published on the external site (optional)
            </Typography>
            <input
              type="date"
              value={externalDate}
              onChange={(e) => setExternalDate(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: 6,
                border: "1px solid #ccc",
                fontSize: "14px",
              }}
            />
          </Box>
        </Box>
      )}

      {images.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: 2,
            mb: 2,
          }}
        >
          {images.map((im, index) => (
            <Box
              key={im.src + index}
              sx={{
                position: "relative",
                border: "1px solid #eee",
                borderRadius: 1,
                p: 1,
              }}
            >
              <img
                src={im.src}
                alt={`blog-img-${index}`}
                style={{ width: "100%", borderRadius: "6px", display: "block" }}
              />
              <IconButton
                size="small"
                sx={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  backgroundColor: "rgba(0,0,0,0.6)",
                  color: "#fff",
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" },
                }}
                onClick={() => handleRemoveImage(im.src)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>

              <input
                type="text"
                placeholder="Caption (shows under image on the blog page)"
                value={im.caption}
                onChange={(e) => {
                  const v = e.target.value;
                  setImages((prev) =>
                    prev.map((p, i) => (i === index ? { ...p, caption: v } : p))
                  );
                }}
                style={{
                  width: "100%",
                  marginTop: 8,
                  padding: "8px",
                  borderRadius: 6,
                  border: "1px solid #ccc",
                }}
              />
            </Box>
          ))}
        </Box>
      )}

      <Box
        sx={{
          mb: 3,
          "& .ql-editor": { minHeight: 300, cursor: "text", lineHeight: 1.8 },
          "& .ql-editor.ql-blank::before": {
            fontStyle: "normal !important",
            fontSize: "16px !important",
            color: "#999",
          },
        }}
      >
        <QuillEditor
          ref={quillRef}
          value={content}
          onChange={setContent}
          modules={modules}
          formats={formats}
        />
      </Box>

      <Button variant="contained" onClick={handleSubmit}>
        Post
      </Button>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          onClose={() => setSnackbarOpen(false)}
          sx={{ width: "100%", backgroundColor: "green", color: "#fff" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};
